import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { map, Observable, of, tap } from 'rxjs'
import { IntervalUsage } from 'src/app/shared/models/intervalUsage'
import { IntervalUsageHistory } from 'src/app/shared/models/intervalUsageHistory'
import { IntervalUsageHistoryResponse } from 'src/app/shared/models/intervalUsageHistoryResponse'
import { IntervalUsageResponse } from 'src/app/shared/models/intervalUsageResponse'
import { ServicePointIdReference } from 'src/app/shared/models/referenceData'
import {
  filter,
  FilterParams,
  httpParams,
  pagination,
  PaginationParams,
  sort,
  SortParams,
  withHttpParams,
} from 'src/app/shared/utilities/http-params'
import { GLOBAL_LOADING_SPINNER_ENABLED } from 'src/app/shared/interceptors/loading-spinner-interceptor'
import { PARSE_DATE_PROPS } from 'src/app/shared/interceptors/date-interceptor'
import { format, startOfHour } from 'date-fns'
import { est, formatAsUTC } from 'src/app/shared/utilities/date'
import { DownloadService } from 'src/app/shared/services/download.service'
import { PARSE_NUMBER_PROPS } from 'src/app/shared/interceptors/number-interceptor'

@Injectable({
  providedIn: 'root',
})
export class IntervalUsageService {
  http = inject(HttpClient)
  downloadService = inject(DownloadService)

  delete(service_point_uid: string, operating_day: string) {
    return this.http
      .delete(`service_point_measurement/${service_point_uid}/${operating_day}`)
      .pipe(
        tap(),
      )
  }

  getIntervalUsage(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ) {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<IntervalUsageResponse>('service_point_measurement/', {
      params,
      context: new HttpContext()
        .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
        .set(PARSE_DATE_PROPS, [
          'add_time',
          'last_update_time',
          'market_interval',
        ])
        .set(PARSE_NUMBER_PROPS, ['measurement_value']),
    })
  }

  getIntervalUsageHistory(
    servicePointRef: ServicePointIdReference,
    marketInterval: Date,
  ): Observable<IntervalUsageHistory[]> {
    const params = httpParams(
      withHttpParams(pagination, { pageNumber: 0, pageSize: 1000 }),
      withHttpParams(filter, [
        {
          name: 'service_point_id',
          values: [{ value: servicePointRef.service_point_id }],
        },
        {
          name: 'service_point_uid',
          values: [{ value: servicePointRef.service_point_uid }],
        },
        {
          name: 'market_interval',
          values: [{ value: formatAsUTC(marketInterval) }],
        },
      ]),
      withHttpParams(sort, [{ name: 'last_update_time', direction: 'desc' }]),
    )
    return this.http
      .get<IntervalUsageHistoryResponse>('service_point_measurement/history', {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, [
            'add_time',
            'last_update_time',
            'market_interval',
          ])
          .set(PARSE_NUMBER_PROPS, ['measurement_value']),
      })
      .pipe(
        map((res) => res.results),
      )
  }

  createIntervalUsage(partials: Partial<IntervalUsage>[]) {
    const reqBody = partials.map((p) => ({
      service_point_uid: p.service_point_uid,
      market_interval: p.market_interval,
      measurement_value: p.measurement_value,
      measurement_value_type_code: 'A',
      channel_measurement_channel_code: '-1',
      interval_type_code: 'HR',
    }))
    return this.http
      .post('service_point_measurement/import', reqBody)
      .pipe(
      )
  }

  downloadImportTemplate() {
    return of(
      'data:text/csv;base64,' +
      btoa(`service_point_id,market_interval,measurement_value\n00000110173450847,${startOfHour(est()).toISOString()},1000.0000`),
    ).pipe(
      tap((url) => {
        const link = document.createElement('a')
        link.download = 'interval-usage-import-template.csv'
        link.href = url
        link.click()
      }),
    )
  }

  downloadExports(filters: FilterParams[]) {
    const params = httpParams(withHttpParams(filter, filters))
    return this.http
      .get<string[]>('service_point_measurement/export', {
        params,
        context: new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, true),
      })
      .pipe(
        tap((urls) => {
          const timestamp = format(est(), 't')
          void this.downloadService.download(
            urls.map((url, i) => ({
              url,
              name: `interval-usage-export-${i}-${timestamp}.csv`,
            })),
          )
        }),
      )
  }
}
